/**
 * Topic-similarity map geometry for ClustersGraph's 'topic' layout
 * (deepcontext-4776.4): stories sit near their stored UMAP coords
 * (`map_x`/`map_y`, deepcontext-4776.3), so related stories read as
 * neighbourhoods instead of rank rings.
 *
 * The settle is a bounded, synchronous, hand-rolled forceX/forceY/forceCollide
 * using d3-force's own maths (alpha decay, velocityDecay 0.4, collide on
 * predicted positions). It is kept dependency-free on purpose: d3 is ESM-only
 * and stubbed in Jest, so this way the jsdom tests exercise the real settle.
 * It never runs an all-pairs loop over every node — only the <=70 "major"
 * circles are compared pairwise; specks use a uniform grid so each one only
 * checks neighbours within 2 × the largest speck radius, and a per-cell list
 * of the (pinned) majors it could touch. A speck whose target lies inside the
 * settled majors starts on the nearest clear spot instead, and the few specks
 * still wedged after the capped cleanup are re-seated on a bounded spiral.
 */

export interface TopicItem {
    r: number;
    /** Target position in frame px (from the story's map coords). */
    tx: number;
    ty: number;
    /** Top 20 + the largest tail stories — settled first, then pinned. */
    major: boolean;
}

export interface TopicDisc {
    x: number;
    y: number;
    r: number;
}

// Jest runs modules inside a vm context, where every global lookup (Math,
// Infinity) goes through a slow interceptor — bind the hot ones once.
const {cos, floor, hypot, max, min, sin, sqrt} = Math;
const INF = Number.POSITIVE_INFINITY;

/** Margin kept between the map-coord extent and the frame edge. */
export const TOPIC_FRAME_MARGIN = 60;

const ALPHA_MIN = 0.001;
/** d3-force's default velocityDecay is 0.4 — velocities keep 60% per tick. */
const VELOCITY_KEEP = 0.6;
const MAJOR_TICKS = 200;
const MAJOR_PULL = 0.3;
/** forceCollide(r + 3): each circle is padded by 3px. */
const MAJOR_PAD = 3;
const MAJOR_RELAX_MAX_PASSES = 500;
const SPECK_TICKS = 120;
/** forceX/forceY switch off for the last this-many speck ticks, so collide alone finishes. */
const SPECK_FREE_TICKS = 30;
const SPECK_PULL = 0.1;
const SPECK_PAD = 1;
const SPECK_COLLIDE_ITERATIONS = 3;
const CLEANUP_MAX_PASSES = 30;
/** A speck still wedged after cleanup is re-seated on a spiral around its spot, this many tries at most. */
const RESEAT_MAX_TRIES = 1800;
const RESEAT_STEP = 2;
/** Specks may kiss by up to a pixel; stopping there keeps the cleanup short. */
const SPECK_SPECK_TOLERANCE = 0.5;
const RELAX_TOLERANCE = 0.01;
/** Offsets two circles sharing an exact target so collide has a direction to push. */
const SEED_JITTER = 0.5;
/** Golden angle (radians) — spreads re-seat candidates evenly around a spiral. */
const GOLDEN_ANGLE = Math.PI * (3 - sqrt(5));
/** Grid never exceeds this many columns/rows — a runaway extent widens cells instead. */
const GRID_MAX_SPAN = 256;

/** Struct-of-arrays circle set — the settle's hot loops stay allocation-free. */
interface Pack {
    n: number;
    x: Float64Array;
    y: Float64Array;
    vx: Float64Array;
    vy: Float64Array;
    r: Float64Array;
    tx: Float64Array;
    ty: Float64Array;
}

function seededUnit(i: number, salt: number): number {
    const v = sin(i * 12.9898 + salt * 78.233) * 43758.5453;
    return v - floor(v);
}

function alphaDecayFor(ticks: number): number {
    return 1 - Math.pow(ALPHA_MIN, 1 / ticks);
}

function packOf(items: TopicItem[], indices: number[]): Pack {
    const n = indices.length;
    const p: Pack = {
        n,
        x: new Float64Array(n),
        y: new Float64Array(n),
        vx: new Float64Array(n),
        vy: new Float64Array(n),
        r: new Float64Array(n),
        tx: new Float64Array(n),
        ty: new Float64Array(n),
    };
    indices.forEach((src, i) => {
        const it = items[src];
        p.r[i] = it.r;
        p.tx[i] = it.tx;
        p.ty[i] = it.ty;
        p.x[i] = it.tx + (seededUnit(src, 3) - 0.5) * 2 * SEED_JITTER;
        p.y[i] = it.ty + (seededUnit(src, 4) - 0.5) * 2 * SEED_JITTER;
    });
    return p;
}

function maxRadius(p: Pack): number {
    let m = 0;
    for (let i = 0; i < p.n; i += 1) if (p.r[i] > m) m = p.r[i];
    return m;
}

function pull(p: Pack, strength: number): void {
    for (let i = 0; i < p.n; i += 1) {
        p.vx[i] += (p.tx[i] - p.x[i]) * strength;
        p.vy[i] += (p.ty[i] - p.y[i]) * strength;
    }
}

function integrate(p: Pack): void {
    for (let i = 0; i < p.n; i += 1) {
        p.vx[i] *= VELOCITY_KEEP;
        p.vy[i] *= VELOCITY_KEEP;
        p.x[i] += p.vx[i];
        p.y[i] += p.vy[i];
    }
}

/**
 * d3 forceCollide's pair step on predicted positions (`a` from pack A at
 * index i, `b` from pack B at j). `bPinned` hands the whole correction to
 * `a` — a pinned circle never moves.
 */
function collide(A: Pack, i: number, B: Pack, j: number, pad: number, bPinned: boolean): void {
    let dx = A.x[i] + A.vx[i] - (B.x[j] + B.vx[j]);
    let dy = A.y[i] + A.vy[i] - (B.y[j] + B.vy[j]);
    const contact = A.r[i] + B.r[j] + 2 * pad;
    if (dx >= contact || dx <= -contact || dy >= contact || dy <= -contact) return;
    let l = dx * dx + dy * dy;
    if (l >= contact * contact) return;
    if (l === 0) {
        dx = 0.01;
        dy = 0;
        l = dx * dx;
    }
    const d = sqrt(l);
    const k = (contact - d) / d;
    dx *= k;
    dy *= k;
    const ra = A.r[i] * A.r[i];
    const rb = B.r[j] * B.r[j];
    const wa = bPinned ? 1 : rb / (ra + rb);
    A.vx[i] += dx * wa;
    A.vy[i] += dy * wa;
    if (!bPinned) {
        B.vx[j] -= dx * (1 - wa);
        B.vy[j] -= dy * (1 - wa);
    }
}

/** Positional separation keeping `gap` px between edges; returns the overlap it resolved. */
function separate(A: Pack, i: number, B: Pack, j: number, gap: number, bPinned: boolean): number {
    const dx = A.x[i] - B.x[j];
    const dy = A.y[i] - B.y[j];
    const contact = A.r[i] + B.r[j] + gap;
    if (dx >= contact || dx <= -contact || dy >= contact || dy <= -contact) return 0;
    const d = sqrt(dx * dx + dy * dy);
    if (d >= contact) return 0;
    const ux = d > 0 ? dx / d : 1;
    const uy = d > 0 ? dy / d : 0;
    const overlap = contact - d;
    const share = bPinned ? overlap : overlap / 2;
    A.x[i] += ux * share;
    A.y[i] += uy * share;
    if (!bPinned) {
        B.x[j] -= ux * share;
        B.y[j] -= uy * share;
    }
    return overlap;
}

/**
 * Flat uniform grid (counting sort into cells). With `cell` >= the largest
 * possible contact distance, every pair that can touch sits in adjacent cells.
 */
interface Grid {
    cell: number;
    x0: number;
    y0: number;
    cols: number;
    rows: number;
    start: Int32Array;
    items: Int32Array;
}

function buildGrid(p: Pack, minCell: number, predicted: boolean): Grid {
    let x0 = INF;
    let y0 = INF;
    let x1 = -INF;
    let y1 = -INF;
    for (let i = 0; i < p.n; i += 1) {
        const x = p.x[i] + (predicted ? p.vx[i] : 0);
        const y = p.y[i] + (predicted ? p.vy[i] : 0);
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
    }
    const cell = max(minCell, (x1 - x0) / GRID_MAX_SPAN, (y1 - y0) / GRID_MAX_SPAN, 1e-6);
    const cols = floor((x1 - x0) / cell) + 1;
    const rows = floor((y1 - y0) / cell) + 1;
    const cellOf = new Int32Array(p.n);
    const start = new Int32Array(cols * rows + 1);
    for (let i = 0; i < p.n; i += 1) {
        const gx = floor((p.x[i] + (predicted ? p.vx[i] : 0) - x0) / cell);
        const gy = floor((p.y[i] + (predicted ? p.vy[i] : 0) - y0) / cell);
        const c = gy * cols + gx;
        cellOf[i] = c;
        start[c + 1] += 1;
    }
    for (let c = 0; c < cols * rows; c += 1) start[c + 1] += start[c];
    const fill = start.slice(0, cols * rows);
    const items = new Int32Array(p.n);
    for (let i = 0; i < p.n; i += 1) items[fill[cellOf[i]]++] = i;
    return {cell, x0, y0, cols, rows, start, items};
}

/**
 * Visits every grid member in the 3×3 cells around (x, y). A plain loop over
 * a callback-free cell range — this runs a few hundred thousand times per settle.
 */
function neighbourCells(g: Grid, x: number, y: number, out: Int32Array): number {
    const gx = floor((x - g.x0) / g.cell);
    const gy = floor((y - g.y0) / g.cell);
    let n = 0;
    for (let cy = max(0, gy - 1); cy <= min(g.rows - 1, gy + 1); cy += 1) {
        for (let cx = max(0, gx - 1); cx <= min(g.cols - 1, gx + 1); cx += 1) out[n++] = cy * g.cols + cx;
    }
    return n;
}

function settleMajors(m: Pack): void {
    const decay = alphaDecayFor(MAJOR_TICKS);
    let alpha = 1;
    for (let t = 0; t < MAJOR_TICKS; t += 1) {
        alpha -= alpha * decay;
        pull(m, MAJOR_PULL * alpha);
        for (let a = 0; a < m.n; a += 1) {
            for (let b = a + 1; b < m.n; b += 1) collide(m, a, m, b, MAJOR_PAD, false);
        }
        integrate(m);
    }
    // Pinned from here on: no leftover velocity may leak into the speck collide.
    m.vx.fill(0);
    m.vy.fill(0);
    // forceCollide only approximately separates — finish the job (<=70 circles, capped).
    for (let pass = 0; pass < MAJOR_RELAX_MAX_PASSES; pass += 1) {
        let worst = 0;
        for (let a = 0; a < m.n; a += 1) {
            for (let b = a + 1; b < m.n; b += 1) worst = max(worst, separate(m, a, m, b, 2 * MAJOR_PAD, false));
        }
        if (worst <= RELAX_TOLERANCE) return;
    }
}

/**
 * Pinned-major lookup for specks: each major is filed under every cell its
 * contact zone (r + largest speck r + padding) covers, so a speck only ever
 * checks the majors listed under its own cell.
 */
interface CoverGrid {
    cell: number;
    x0: number;
    y0: number;
    cols: number;
    rows: number;
    start: Int32Array;
    items: Int32Array;
}

function buildCoverGrid(m: Pack, reach: number, cell: number): CoverGrid {
    let x0 = INF;
    let y0 = INF;
    let x1 = -INF;
    let y1 = -INF;
    for (let j = 0; j < m.n; j += 1) {
        const R = m.r[j] + reach;
        x0 = min(x0, m.x[j] - R);
        y0 = min(y0, m.y[j] - R);
        x1 = max(x1, m.x[j] + R);
        y1 = max(y1, m.y[j] + R);
    }
    const size = max(cell, (x1 - x0) / GRID_MAX_SPAN, (y1 - y0) / GRID_MAX_SPAN);
    const cols = floor((x1 - x0) / size) + 1;
    const rows = floor((y1 - y0) / size) + 1;
    const start = new Int32Array(cols * rows + 1);
    const span = (j: number) => {
        const R = m.r[j] + reach;
        return [
            floor((m.x[j] - R - x0) / size),
            min(cols - 1, floor((m.x[j] + R - x0) / size)),
            floor((m.y[j] - R - y0) / size),
            min(rows - 1, floor((m.y[j] + R - y0) / size)),
        ];
    };
    for (let j = 0; j < m.n; j += 1) {
        const [ax, bx, ay, by] = span(j);
        for (let gy = ay; gy <= by; gy += 1) for (let gx = ax; gx <= bx; gx += 1) start[gy * cols + gx + 1] += 1;
    }
    for (let c = 0; c < cols * rows; c += 1) start[c + 1] += start[c];
    const fill = start.slice(0, cols * rows);
    const items = new Int32Array(start[cols * rows]);
    for (let j = 0; j < m.n; j += 1) {
        const [ax, bx, ay, by] = span(j);
        for (let gy = ay; gy <= by; gy += 1) for (let gx = ax; gx <= bx; gx += 1) items[fill[gy * cols + gx]++] = j;
    }
    return {cell: size, x0, y0, cols, rows, start, items};
}

/** Cell index of (x, y) in a cover grid, or -1 when outside every major's reach. */
function coverCell(g: CoverGrid, x: number, y: number): number {
    const gx = floor((x - g.x0) / g.cell);
    const gy = floor((y - g.y0) / g.cell);
    return gx < 0 || gy < 0 || gx >= g.cols || gy >= g.rows ? -1 : gy * g.cols + gx;
}

/** Deepest overlap of a speck of radius `r` at (x, y) against the pinned majors (<= 0 = clear). */
function majorOverlap(r: number, x: number, y: number, m: Pack, cover: CoverGrid | null): number {
    let worst = -INF;
    const c = cover ? coverCell(cover, x, y) : -1;
    if (!cover || c < 0) return worst;
    for (let k = cover.start[c]; k < cover.start[c + 1]; k += 1) {
        const j = cover.items[k];
        worst = max(worst, r + m.r[j] - hypot(x - m.x[j], y - m.y[j]));
    }
    return worst;
}

/**
 * Deepest overlap of speck i at (x, y) against every other speck: grid
 * neighbours that have not moved since `g` was built, plus the re-seated ones.
 */
function speckOverlap(
    s: Pack,
    i: number,
    x: number,
    y: number,
    g: Grid,
    moved: number[],
    isMoved: Uint8Array,
    cells: Int32Array,
): number {
    let worst = -INF;
    const nc = neighbourCells(g, x, y, cells);
    for (let c = 0; c < nc; c += 1) {
        for (let k = g.start[cells[c]]; k < g.start[cells[c] + 1]; k += 1) {
            const j = g.items[k];
            if (j !== i && !isMoved[j]) worst = max(worst, s.r[i] + s.r[j] - hypot(x - s.x[j], y - s.y[j]));
        }
    }
    for (const j of moved) if (j !== i) worst = max(worst, s.r[i] + s.r[j] - hypot(x - s.x[j], y - s.y[j]));
    return worst;
}

/**
 * First point on a golden-angle spiral around (x, y) that `fits` accepts, or
 * null. Bounded by RESEAT_MAX_TRIES.
 */
function spiralSearch(x: number, y: number, fits: (x: number, y: number) => boolean): [number, number] | null {
    for (let k = 1; k <= RESEAT_MAX_TRIES; k += 1) {
        const dist = RESEAT_STEP * 2 * sqrt(k);
        const cx = x + cos(k * GOLDEN_ANGLE) * dist;
        const cy = y + sin(k * GOLDEN_ANGLE) * dist;
        if (fits(cx, cy)) return [cx, cy];
    }
    return null;
}

function settleSpecks(s: Pack, m: Pack): void {
    if (s.n === 0) return;
    const speckR = maxRadius(s);
    const speckCell = 2 * (speckR + SPECK_PAD) + 1;
    // Majors are pinned from here on, so one lookup serves every pass.
    const cover = m.n > 0 ? buildCoverGrid(m, speckR + 2 * SPECK_PAD + 1, speckCell) : null;
    // A target inside the settled majors would leave the speck wedged between
    // pinned circles — start it on the nearest spot clear of them instead.
    for (let i = 0; i < s.n; i += 1) {
        const r = s.r[i];
        if (majorOverlap(r, s.x[i], s.y[i], m, cover) <= -SPECK_PAD) continue;
        const spot = spiralSearch(s.x[i], s.y[i], (x, y) => majorOverlap(r, x, y, m, cover) <= -SPECK_PAD);
        if (spot) [s.x[i], s.y[i]] = spot;
    }
    const cells = new Int32Array(9);
    const decay = alphaDecayFor(SPECK_TICKS);
    let alpha = 1;
    for (let t = 0; t < SPECK_TICKS; t += 1) {
        alpha -= alpha * decay;
        if (t < SPECK_TICKS - SPECK_FREE_TICKS) pull(s, SPECK_PULL * alpha);
        for (let it = 0; it < SPECK_COLLIDE_ITERATIONS; it += 1) {
            const g = buildGrid(s, speckCell, true);
            for (let i = 0; i < s.n; i += 1) {
                const px = s.x[i] + s.vx[i];
                const py = s.y[i] + s.vy[i];
                const nc = neighbourCells(g, px, py, cells);
                for (let c = 0; c < nc; c += 1) {
                    for (let k = g.start[cells[c]]; k < g.start[cells[c] + 1]; k += 1) {
                        const j = g.items[k];
                        if (j > i) collide(s, i, s, j, SPECK_PAD, false);
                    }
                }
                const mc = cover ? coverCell(cover, px, py) : -1;
                if (cover && mc >= 0) {
                    for (let k = cover.start[mc]; k < cover.start[mc + 1]; k += 1) collide(s, i, m, cover.items[k], SPECK_PAD, true);
                }
            }
        }
        integrate(s);
    }
    // Capped cleanup: speck-speck nudged apart (<=1px overlap is fine), then
    // every speck pushed fully off the pinned majors — that push runs last.
    let settled = false;
    for (let pass = 0; pass < CLEANUP_MAX_PASSES && !settled; pass += 1) {
        let worstSpeck = 0;
        let worstMajor = 0;
        const g = buildGrid(s, speckCell, false);
        for (let i = 0; i < s.n; i += 1) {
            const nc = neighbourCells(g, s.x[i], s.y[i], cells);
            for (let c = 0; c < nc; c += 1) {
                for (let k = g.start[cells[c]]; k < g.start[cells[c] + 1]; k += 1) {
                    const j = g.items[k];
                    if (j > i) worstSpeck = max(worstSpeck, separate(s, i, s, j, 0, false));
                }
            }
        }
        for (let i = 0; i < s.n && cover; i += 1) {
            const mc = coverCell(cover, s.x[i], s.y[i]);
            if (mc < 0) continue;
            for (let k = cover.start[mc]; k < cover.start[mc + 1]; k += 1) {
                worstMajor = max(worstMajor, separate(s, i, m, cover.items[k], SPECK_PAD, true));
            }
        }
        settled = worstSpeck <= SPECK_SPECK_TOLERANCE && worstMajor <= SPECK_PAD;
    }
    if (settled) return;
    // A speck wedged between circles can oscillate forever — re-seat just those
    // on the nearest free spot of a spiral around where they ended up.
    const g = buildGrid(s, speckCell, false);
    const moved: number[] = [];
    const isMoved = new Uint8Array(s.n);
    const overlapAt = (i: number, x: number, y: number) => speckOverlap(s, i, x, y, g, moved, isMoved, cells);
    for (let i = 0; i < s.n; i += 1) {
        const r = s.r[i];
        if (majorOverlap(r, s.x[i], s.y[i], m, cover) <= 0 && overlapAt(i, s.x[i], s.y[i]) <= SPECK_SPECK_TOLERANCE) {
            continue;
        }
        const spot = spiralSearch(
            s.x[i],
            s.y[i],
            (x, y) => majorOverlap(r, x, y, m, cover) <= -SPECK_PAD && overlapAt(i, x, y) <= 0,
        );
        if (!spot) continue;
        [s.x[i], s.y[i]] = spot;
        isMoved[i] = 1;
        moved.push(i);
    }
}

/**
 * Settle `items` around their targets. Majors: pulled to target + collide,
 * 200 ticks, then relaxed until nothing touches. Specks: start at target,
 * collide (3 iterations) + a weak pull for 120 ticks with majors pinned, the
 * pull off for the last 30, then a grid-bounded cleanup. Returns one disc
 * per item, same order. Deterministic.
 */
export function settleTopicLayout(items: TopicItem[]): TopicDisc[] {
    const majorIdx: number[] = [];
    const speckIdx: number[] = [];
    items.forEach((it, i) => (it.major ? majorIdx : speckIdx).push(i));
    const majors = packOf(items, majorIdx);
    const specks = packOf(items, speckIdx);
    settleMajors(majors);
    settleSpecks(specks, majors);
    const out: TopicDisc[] = new Array(items.length);
    majorIdx.forEach((src, i) => (out[src] = {x: majors.x[i], y: majors.y[i], r: majors.r[i]}));
    speckIdx.forEach((src, i) => (out[src] = {x: specks.x[i], y: specks.y[i], r: specks.r[i]}));
    return out;
}

export interface TopicPoint {
    mapX: number | null | undefined;
    mapY: number | null | undefined;
    /** IAB category key, or null when unclassified. */
    cat: string | null;
    weight: number;
}

export function hasMapCoords(p: {map_x?: number | null; map_y?: number | null}): boolean {
    return Number.isFinite(p.map_x) && Number.isFinite(p.map_y);
}

/**
 * Map coords in [-1,1] onto the frame (TOPIC_FRAME_MARGIN inset), x to width
 * and y to height. A point with no coords targets the weighted centroid of
 * its category's placed points, else the frame centre.
 */
export function topicTargets(points: TopicPoint[], w: number, h: number): {x: number; y: number}[] {
    const toX = (v: number) => TOPIC_FRAME_MARGIN + ((v + 1) / 2) * max(0, w - 2 * TOPIC_FRAME_MARGIN);
    const toY = (v: number) => TOPIC_FRAME_MARGIN + ((v + 1) / 2) * max(0, h - 2 * TOPIC_FRAME_MARGIN);
    const placed = points.map((p) =>
        Number.isFinite(p.mapX) && Number.isFinite(p.mapY) ? {x: toX(p.mapX as number), y: toY(p.mapY as number)} : null,
    );
    const sums = new Map<string, {x: number; y: number; w: number}>();
    points.forEach((p, i) => {
        const at = placed[i];
        if (!at || p.cat == null) return;
        const wgt = max(p.weight, 1);
        const s = sums.get(p.cat) ?? {x: 0, y: 0, w: 0};
        s.x += at.x * wgt;
        s.y += at.y * wgt;
        s.w += wgt;
        sums.set(p.cat, s);
    });
    return points.map((p, i) => {
        const at = placed[i];
        if (at) return at;
        const s = p.cat != null ? sums.get(p.cat) : undefined;
        return s ? {x: s.x / s.w, y: s.y / s.w} : {x: w / 2, y: h / 2};
    });
}

export interface CategoryMember {
    cat: string | null;
    label: string;
    x: number;
    y: number;
    r: number;
    weight: number;
}

export interface CategoryLabel {
    key: string;
    label: string;
    /** Member-weighted centroid of the settled positions. */
    x: number;
    y: number;
    /** Baseline just above the neighbourhood's core, so the label never covers bubble text. */
    top: number;
}

/** A category needs at least this many drawn stories to earn a label. */
const CATEGORY_LABEL_MIN_STORIES = 3;
/** ...and its members' mean distance to the centroid must stay under this fraction of min(w, h). */
const CATEGORY_LABEL_TIGHTNESS = 0.25;
/** Members whose near edge lies farther than this × the mean distance are outliers — they don't lift the label. */
const CATEGORY_CORE_SPREAD = 1.5;
/** Clear space between the label baseline and the top of the neighbourhood. */
const CATEGORY_LABEL_LIFT = 8;

/**
 * One label per tight IAB category: member-weighted centroid of the settled
 * positions, kept only when the category really forms a neighbourhood, with
 * its baseline lifted above the neighbourhood's core members.
 */
export function categoryLabels(
    members: CategoryMember[],
    w: number,
    h: number,
): CategoryLabel[] {
    const byCat = new Map<string, CategoryMember[]>();
    for (const m of members) {
        if (m.cat == null) continue;
        const list = byCat.get(m.cat);
        if (list) list.push(m);
        else byCat.set(m.cat, [m]);
    }
    const limit = CATEGORY_LABEL_TIGHTNESS * min(w, h);
    const out: CategoryLabel[] = [];
    byCat.forEach((list, key) => {
        if (list.length < CATEGORY_LABEL_MIN_STORIES) return;
        const total = list.reduce((s, m) => s + max(m.weight, 1), 0);
        const x = list.reduce((s, m) => s + m.x * max(m.weight, 1), 0) / total;
        const y = list.reduce((s, m) => s + m.y * max(m.weight, 1), 0) / total;
        const meanDist = list.reduce((s, m) => s + hypot(m.x - x, m.y - y), 0) / list.length;
        if (meanDist >= limit) return;
        const core = list.filter((m) => hypot(m.x - x, m.y - y) - m.r <= CATEGORY_CORE_SPREAD * meanDist);
        const top = (core.length > 0 ? core : list).reduce((t, m) => min(t, m.y - m.r), INF) - CATEGORY_LABEL_LIFT;
        out.push({key, label: list[0].label, x, y, top});
    });
    return out;
}
